import { Installments, ScheduleType } from '../../../types/flyout'
import { clampScheduledInstallmentCount } from '../../utils/constants'

export type RenewalScheduleRow = {
  monthsAfter?: number
  dayOfMonth: number
  percentage: number
  paymentDate: string
  amount: number | null
  currency: string
}

export const isRenewalMonthsAfterFilled = (value: unknown) => {
  if (value === '' || value === null || value === undefined) return false
  return Number.isFinite(Number(value))
}

const scheduleStep = (type?: ScheduleType) => {
  if (type === ScheduleType.MONTHLY) return 1
  if (type === ScheduleType.QUARTERLY) return 3
  if (type === ScheduleType.YEARLY) return 12
  return 0
}

/** Equal % split; remainder % on first row (Figma). */
export const buildRenewalEqualScheduleRows = ({
  scheduleType,
  serviceLengthMonths,
  dayOfMonth,
  tcv,
  currency,
}: {
  scheduleType: ScheduleType
  serviceLengthMonths: number
  dayOfMonth: number
  tcv: number
  currency: string
}): RenewalScheduleRow[] => {
  const step = scheduleStep(scheduleType)
  if (!step || serviceLengthMonths < 1) return []

  const count = clampScheduledInstallmentCount(
    Math.max(1, Math.ceil(serviceLengthMonths / step))
  )
  const basePct = Math.floor(100 / count)
  const remainder = 100 - basePct * count

  return Array.from({ length: count }, (_, index) => {
    const percentage = basePct + (index === 0 ? remainder : 0)
    return {
      monthsAfter: index * step,
      dayOfMonth,
      percentage,
      paymentDate: '',
      amount: Math.round(((tcv * percentage) / 100) * 100) / 100,
      currency,
    }
  })
}

export const emptyRenewalInstallment = (
  dayOfMonth: number,
  currency: string
): RenewalScheduleRow => ({
  monthsAfter: 0,
  dayOfMonth,
  percentage: 100,
  paymentDate: '',
  amount: null,
  currency,
})

/** Keep YYYY-MM, swap the day (clamped to last day of that month). */
export const applyDayToIsoDate = (iso: string, day: number) => {
  const stamp = iso.slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(stamp)) return iso
  const year = Number(stamp.slice(0, 4))
  const month = Number(stamp.slice(5, 7))
  const last = new Date(year, month, 0).getDate()
  const dd = String(Math.min(Math.max(1, day), last)).padStart(2, '0')
  return `${stamp.slice(0, 8)}${dd}${iso.slice(10)}`
}

export const dayFromIsoDate = (iso?: string) => {
  if (!iso) return null
  const day = Number(String(iso).slice(8, 10))
  return Number.isFinite(day) && day >= 1 && day <= 31 ? day : null
}

export const monthsBetweenIsoDates = (
  start?: string,
  end?: string,
  fallback = 0
) => {
  const startDate = String(start || '').slice(0, 10)
  const endDate = String(end || '').slice(0, 10)
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(startDate) ||
    !/^\d{4}-\d{2}-\d{2}$/.test(endDate)
  ) {
    return fallback
  }
  return (
    (Number(endDate.slice(0, 4)) - Number(startDate.slice(0, 4))) * 12 +
    Number(endDate.slice(5, 7)) -
    Number(startDate.slice(5, 7))
  )
}

export const getInstallmentPercentages = (amounts: number[]) => {
  if (!amounts.length) return []
  const total = amounts.reduce((sum, amount) => sum + (Number(amount) || 0), 0)
  const percentages = amounts.map((amount, index) =>
    total > 0
      ? Math.round(((Number(amount) || 0) / total) * 10000) / 100
      : Math.floor(100 / amounts.length) +
        (index === 0 ? 100 % amounts.length : 0)
  )
  const remainder =
    Math.round((100 - percentages.reduce((sum, value) => sum + value, 0)) * 100) /
    100
  percentages[0] += remainder
  return percentages
}

export const buildCustomRenewalSync = ({
  mainInstallments,
  renewalInstallments,
  contractStartDate,
  firstInstallmentDate,
  initialInvoiceDate,
  dayOfMonth,
  currency,
  tcv,
}: {
  mainInstallments: Installments[]
  renewalInstallments: Installments[]
  contractStartDate?: string
  firstInstallmentDate?: string | null
  initialInvoiceDate?: string
  dayOfMonth: number
  currency: string
  tcv: number
}) => {
  const percentages = getInstallmentPercentages(
    mainInstallments.map(row => Number(row.amount) || 0)
  )
  const firstDate = firstInstallmentDate || mainInstallments[0]?.paymentDate

  return {
    dayOfMonth: dayFromIsoDate(firstDate) || dayOfMonth,
    installments: mainInstallments.map((mainRow, index) => {
      const rowPercentage = percentages[index]
      return {
        ...(renewalInstallments[index] ||
          emptyRenewalInstallment(dayOfMonth, currency)),
        monthsAfter: monthsBetweenIsoDates(
          contractStartDate || initialInvoiceDate,
          mainRow.paymentDate,
          index
        ),
        amount: Math.round(((tcv * rowPercentage) / 100) * 100) / 100,
        dayOfMonth: dayFromIsoDate(mainRow.paymentDate) || dayOfMonth,
        percentage: rowPercentage,
      }
    }),
  }
}

export const buildRenewalRowsFromMainInstallments = ({
  mainInstallments,
  scheduleType,
  contractStartDate,
}: {
  mainInstallments: Installments[]
  scheduleType: ScheduleType
  contractStartDate?: string
}) => {
  const percentages = getInstallmentPercentages(
    mainInstallments.map(row => Number(row.amount) || 0)
  )
  const step =
    scheduleType === ScheduleType.MONTHLY
      ? 1
      : scheduleType === ScheduleType.YEARLY
        ? 12
        : 3

  return mainInstallments.map((row, index) => ({
    ...row,
    monthsAfter: monthsBetweenIsoDates(
      contractStartDate || mainInstallments[0]?.paymentDate,
      row.paymentDate,
      index * step
    ),
    dayOfMonth: dayFromIsoDate(row.paymentDate) || 1,
    amount: null,
    percentage: percentages[index],
  }))
}

export const renewalPercentagesSumTo100 = (
  rows?: { percentage?: number | null }[]
) => {
  if (!rows?.length) return true
  const sum = rows.reduce(
    (total, row) => total + (Number(row?.percentage) || 0),
    0
  )
  return Math.abs(sum - 100) < 0.01
}

export const clampDayOfMonth = (n: number) =>
  Number.isFinite(n) ? Math.min(31, Math.max(1, n)) : 1

export const scheduleLabel = (type?: ScheduleType) => {
  if (type === ScheduleType.MONTHLY) return 'Monthly'
  if (type === ScheduleType.QUARTERLY) return 'Quarterly'
  if (type === ScheduleType.YEARLY) return 'Yearly'
  return 'Custom'
}

export const labelToSchedule = (label: string): ScheduleType => {
  if (label === 'Monthly') return ScheduleType.MONTHLY
  if (label === 'Quarterly') return ScheduleType.QUARTERLY
  if (label === 'Yearly') return ScheduleType.YEARLY
  return ScheduleType.CUSTOM
}
